import React, { useEffect, useState, useRef } from 'react';
import { Card, Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHourglass, faMoneyCheck, faSpinner, faTools, faUserTie } from '@fortawesome/free-solid-svg-icons';

import { useFilterSettings } from '../../Store/ui/hooks';
import { useIsPsAdmin } from '../../hooks';

import type { AwaitingPoCardProps } from './types';

import styles from './StatusCards.module.scss';
import { useSetFilterSettings } from '../../Store/entities/quotes/hooks';
import { setVisibleQuoteIdsLoadingStatus } from '../../Store/ui';
import { UsePortalDispatch } from '../../Store';
import { loadStatus } from '../../Store/entities/entitiesType';

const AwaitingStepOrder = {
	approval: 0,
	processing: 1,
	sales: 2,
	service: 3,
	accounting: 4
};

const AwaitingPoCard = ({
	loading,
	salesCount,
	serviceCount,
	accountingCount,
	processingCount,
	active,
	displayCount = true
}: AwaitingPoCardProps) => {
	const filterSettings = useFilterSettings();
	const setFilterSettings = useSetFilterSettings();
	const dispatch = UsePortalDispatch();
	const isPsAdmin = useIsPsAdmin();

	const totalCount = salesCount + serviceCount + accountingCount + processingCount;

	const [count, setCount] = useState(totalCount);

	useEffect(() => {
		if (loading) {
			setCount(0);
		} else {
			setCount(totalCount);
		}
	}, [loading, totalCount]);

	const depts = [
		{
			name: "Approval",
			value: ["all"]
		},
		{
			name: "Processing",
			value: ["submitted_and_processing"]
		},
		{
			name: "Sales",
			value: ["awaiting_discount_approval"]
		},
		{
			name: "Service",
			value: ["under_review"]
		},
		{
			name: "Accounting",
			value: ["awaiting_po"]
		},
	];

	const [currentDept, setCurrentDept] = useState(0);
	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

	const updateFilterSettings = (deptIndex?: number) => {
		const targetDept = deptIndex !== undefined ? deptIndex : currentDept;
		dispatch(setVisibleQuoteIdsLoadingStatus(loadStatus.loading));
		setFilterSettings({
			...filterSettings, filter_names: depts[targetDept]?.value, page_number: 1
		});
	};

	const debouncedUpdateFilterSettings = (deptIndex: number) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}
		
		debounceTimeout.current = setTimeout(() => {
			updateFilterSettings(deptIndex);
		}, 500);
	};

	useEffect(() => () => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}
	}, []);

	const toggleDepartment = () => {
		if (!isPsAdmin) {
			dispatch(setVisibleQuoteIdsLoadingStatus(loadStatus.loading));
			setFilterSettings({
				...filterSettings,
				filter_names: [
					"submitted_and_processing",
					"awaiting_discount_approval",
					"under_review",
					"awaiting_po"
				],
				page_number: 1
			});
			return;
		}
		
		setCurrentDept((prev) => {
			const newDept = prev === 4 ? 0 : prev + 1;
			
			setCount(() => {
				switch (newDept) {
					case AwaitingStepOrder.approval:
						return totalCount;
					case AwaitingStepOrder.accounting:
						return accountingCount;
					case AwaitingStepOrder.sales:
						return salesCount;
					case AwaitingStepOrder.service:
						return serviceCount;
					case AwaitingStepOrder.processing:
						return processingCount;
					default:
						return totalCount;
				}
			});
			
			debouncedUpdateFilterSettings(newDept);
			
			return newDept;
		});
	};

	const selectDepartment = (deptIndex: number) => {
		if (!isPsAdmin) return;
		
		setCurrentDept(deptIndex);
		
		setCount(() => {
			switch (deptIndex) {
				case AwaitingStepOrder.approval:
					return totalCount;
				case AwaitingStepOrder.accounting:
					return accountingCount;
				case AwaitingStepOrder.sales:
					return salesCount;
				case AwaitingStepOrder.service:
					return serviceCount;
				case AwaitingStepOrder.processing:
					return processingCount;
				default:
					return totalCount;
			}
		});
		
		debouncedUpdateFilterSettings(deptIndex);
	};

	return (
		<Col>
			<Card
				body
				onClick={toggleDepartment}
				className={`${styles.statusCard} ${active && styles.active} status-card ${active && "active"}`}
			>
				<Row>
					<Col>
						<h5>{loading ? (<FontAwesomeIcon icon={faSpinner} spin />) : displayCount && count}</h5>
					</Col>
				</Row>
				<Row>
					<Col>
						<h6>
							{isPsAdmin ? (
								<>
									Awaiting&nbsp;
									{depts[currentDept]?.name ?? "Approval"}
								</>
							) : (
								"Awaiting Approval"
							)}
						</h6>
					</Col>
				</Row>
				{isPsAdmin && (
					<Col>
						
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id="processing-tooltip">Processing</Tooltip>}
						>
							<button
								aria-label="Processing"
								type="button"
								data-count={processingCount > 0 ? processingCount : undefined}
								className={`btn ${currentDept === AwaitingStepOrder.processing
									? 'btn-success' : 'btn-secondary'} ${
									processingCount > 0 ? 'status-card-btn' : ''
								} px-1 py-0 text-white m-1`}
								onClick={(e) => {
									e.stopPropagation();
									selectDepartment(AwaitingStepOrder.processing);
								}}
							>
								<FontAwesomeIcon icon={faHourglass} />
							</button>
						</OverlayTrigger>
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id="sales-tooltip">Sales</Tooltip>}
						>
							<button
								aria-label="Sales"
								type="button"
								data-count={salesCount > 0 ? salesCount : undefined}
								className={`btn ${currentDept === AwaitingStepOrder.sales
									? 'btn-success' : 'btn-secondary'} ${
									salesCount > 0 ? 'status-card-btn' : ''
								} px-1 py-0 text-white m-1`}
								onClick={(e) => {
									e.stopPropagation();
									selectDepartment(AwaitingStepOrder.sales);
								}}
							>
								<FontAwesomeIcon icon={faUserTie} />
							</button>
						</OverlayTrigger>
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id="service-tooltip">Service</Tooltip>}
						>
							<button
								aria-label="Service"
								type="button"
								data-count={serviceCount > 0 ? serviceCount : undefined}
								className={`btn ${currentDept === AwaitingStepOrder.service
									? 'btn-success' : 'btn-secondary'} ${
									serviceCount > 0 ? 'status-card-btn' : ''
								} px-1 py-0 text-white m-1`}
								onClick={(e) => {
									e.stopPropagation();
									selectDepartment(AwaitingStepOrder.service);
								}}
							>
								<FontAwesomeIcon icon={faTools} />
							</button>
						</OverlayTrigger>
						<OverlayTrigger
							placement="top"
							overlay={<Tooltip id="accounting-tooltip">Accounting</Tooltip>}
						>
							<button
								aria-label="Accounting"
								type="button"
								data-count={accountingCount > 0 ? accountingCount : undefined}
								className={`btn ${currentDept === AwaitingStepOrder.accounting
									? 'btn-success' : 'btn-secondary'} ${
									accountingCount > 0 ? 'status-card-btn' : ''
								} px-1 py-0 text-white m-1`}
								onClick={(e) => {
									e.stopPropagation();
									selectDepartment(AwaitingStepOrder.accounting);
								}}
							>
								<FontAwesomeIcon icon={faMoneyCheck} />
							</button>
						</OverlayTrigger>
					</Col>
				)}
			</Card>
		</Col>
	);
};

export default AwaitingPoCard;
